import React from 'react';
import './IncomeStatementTable.css';

const TABLE_VIEW = {
  ABSOLUTE: 'incomeStatement',
  GROWTH: 'incomeStatementGrowth'
};

const ABSOLUTE_FIELD_LABELS = {
  date: 'Statement Date',
  reportedCurrency: 'Reported Currency',
  filingDate: 'Filing Date',
  acceptedDate: 'Accepted Date',
  period: 'Period',
  revenue: 'Revenue',
  costOfRevenue: 'Cost of Revenue',
  grossProfit: 'Gross Profit',
  researchAndDevelopmentExpenses: 'R&D Expenses',
  generalAndAdministrativeExpenses: 'General & Administrative Expenses',
  sellingAndMarketingExpenses: 'Selling & Marketing Expenses',
  sellingGeneralAndAdministrativeExpenses: 'SG&A Expenses',
  otherExpenses: 'Other Expenses',
  operatingExpenses: 'Operating Expenses',
  costAndExpenses: 'Cost and Expenses',
  netInterestIncome: 'Net Interest Income',
  interestIncome: 'Interest Income',
  interestExpense: 'Interest Expense',
  depreciationAndAmortization: 'Depreciation & Amortization',
  ebitda: 'EBITDA',
  ebit: 'EBIT',
  nonOperatingIncomeExcludingInterest: 'Non-Operating Income Ex. Interest',
  operatingIncome: 'Operating Income',
  totalOtherIncomeExpensesNet: 'Other Income/Expenses Net',
  incomeBeforeTax: 'Income Before Tax',
  incomeTaxExpense: 'Income Tax Expense',
  netIncomeFromContinuingOperations: 'Net Income From Continuing Ops',
  netIncomeFromDiscontinuedOperations: 'Net Income From Discontinued Ops',
  otherAdjustmentsToNetIncome: 'Other Adjustments To Net Income',
  netIncome: 'Net Income',
  netIncomeDeductions: 'Net Income Deductions',
  bottomLineNetIncome: 'Bottom Line Net Income',
  eps: 'EPS',
  epsDiluted: 'Diluted EPS',
  weightedAverageShsOut: 'Weighted Average Shares Out',
  weightedAverageShsOutDil: 'Weighted Average Shares Out Diluted'
};

const ABSOLUTE_FIELD_ORDER = [
  'date',
  'reportedCurrency',
  'filingDate',
  'acceptedDate',
  'period',
  'revenue',
  'costOfRevenue',
  'grossProfit',
  'researchAndDevelopmentExpenses',
  'generalAndAdministrativeExpenses',
  'sellingAndMarketingExpenses',
  'sellingGeneralAndAdministrativeExpenses',
  'otherExpenses',
  'operatingExpenses',
  'costAndExpenses',
  'netInterestIncome',
  'interestIncome',
  'interestExpense',
  'depreciationAndAmortization',
  'ebitda',
  'ebit',
  'nonOperatingIncomeExcludingInterest',
  'operatingIncome',
  'totalOtherIncomeExpensesNet',
  'incomeBeforeTax',
  'incomeTaxExpense',
  'netIncomeFromContinuingOperations',
  'netIncomeFromDiscontinuedOperations',
  'otherAdjustmentsToNetIncome',
  'netIncome',
  'netIncomeDeductions',
  'bottomLineNetIncome',
  'eps',
  'epsDiluted',
  'weightedAverageShsOut',
  'weightedAverageShsOutDil'
];

const ABSOLUTE_CURRENCY_FIELDS = new Set([
  'revenue',
  'costOfRevenue',
  'grossProfit',
  'researchAndDevelopmentExpenses',
  'generalAndAdministrativeExpenses',
  'sellingAndMarketingExpenses',
  'sellingGeneralAndAdministrativeExpenses',
  'otherExpenses',
  'operatingExpenses',
  'costAndExpenses',
  'netInterestIncome',
  'interestIncome',
  'interestExpense',
  'depreciationAndAmortization',
  'ebitda',
  'ebit',
  'nonOperatingIncomeExcludingInterest',
  'operatingIncome',
  'totalOtherIncomeExpensesNet',
  'incomeBeforeTax',
  'incomeTaxExpense',
  'netIncomeFromContinuingOperations',
  'netIncomeFromDiscontinuedOperations',
  'otherAdjustmentsToNetIncome',
  'netIncome',
  'netIncomeDeductions',
  'bottomLineNetIncome'
]);

const ABSOLUTE_SHARE_FIELDS = new Set([
  'weightedAverageShsOut',
  'weightedAverageShsOutDil'
]);

const ABSOLUTE_DECIMAL_FIELDS = new Set(['eps', 'epsDiluted']);

const GROWTH_FIELD_LABELS = {
  date: 'Statement Date',
  reportedCurrency: 'Reported Currency',
  period: 'Period',
  growthRevenue: 'Revenue',
  growthCostOfRevenue: 'Cost of Revenue',
  growthGrossProfit: 'Gross Profit',
  growthGrossProfitRatio: 'Gross Profit Ratio',
  growthResearchAndDevelopmentExpenses: 'R&D Expenses',
  growthGeneralAndAdministrativeExpenses: 'General & Administrative Expenses',
  growthSellingAndMarketingExpenses: 'Selling & Marketing Expenses',
  growthOtherExpenses: 'Other Expenses',
  growthOperatingExpenses: 'Operating Expenses',
  growthCostAndExpenses: 'Cost and Expenses',
  growthNetInterestIncome: 'Net Interest Income',
  growthInterestIncome: 'Interest Income',
  growthInterestExpense: 'Interest Expense',
  growthDepreciationAndAmortization: 'Depreciation & Amortization',
  growthEBITDA: 'EBITDA',
  growthEBIT: 'EBIT',
  growthNonOperatingIncomeExcludingInterest: 'Non-Operating Income Ex. Interest',
  growthOperatingIncome: 'Operating Income',
  growthTotalOtherIncomeExpensesNet: 'Other Income/Expenses Net',
  growthIncomeBeforeTax: 'Income Before Tax',
  growthIncomeTaxExpense: 'Income Tax Expense',
  growthNetIncomeFromContinuingOperations: 'Net Income From Continuing Ops',
  growthOtherAdjustmentsToNetIncome: 'Other Adjustments To Net Income',
  growthNetIncome: 'Net Income',
  growthNetIncomeDeductions: 'Net Income Deductions',
  growthEPS: 'EPS',
  growthEPSDiluted: 'Diluted EPS',
  growthWeightedAverageShsOut: 'Weighted Average Shares Out',
  growthWeightedAverageShsOutDil: 'Weighted Average Shares Out Diluted'
};

const GROWTH_FIELD_ORDER = [
  'date',
  'reportedCurrency',
  'period',
  'growthRevenue',
  'growthCostOfRevenue',
  'growthGrossProfit',
  'growthGrossProfitRatio',
  'growthResearchAndDevelopmentExpenses',
  'growthGeneralAndAdministrativeExpenses',
  'growthSellingAndMarketingExpenses',
  'growthOtherExpenses',
  'growthOperatingExpenses',
  'growthCostAndExpenses',
  'growthNetInterestIncome',
  'growthInterestIncome',
  'growthInterestExpense',
  'growthDepreciationAndAmortization',
  'growthEBITDA',
  'growthEBIT',
  'growthNonOperatingIncomeExcludingInterest',
  'growthOperatingIncome',
  'growthTotalOtherIncomeExpensesNet',
  'growthIncomeBeforeTax',
  'growthIncomeTaxExpense',
  'growthNetIncomeFromContinuingOperations',
  'growthOtherAdjustmentsToNetIncome',
  'growthNetIncome',
  'growthNetIncomeDeductions',
  'growthEPS',
  'growthEPSDiluted',
  'growthWeightedAverageShsOut',
  'growthWeightedAverageShsOutDil'
];

const GROWTH_PERCENTAGE_FIELDS = new Set(GROWTH_FIELD_ORDER.filter((field) => field.startsWith('growth')));

const DATE_FIELDS = new Set(['date', 'filingDate', 'acceptedDate']);

function formatAbbreviatedNumber(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value;
  }

  const absoluteValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absoluteValue >= 1_000_000_000) {
    return `${sign}${(absoluteValue / 1_000_000_000).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} B`;
  }

  if (absoluteValue >= 1_000_000) {
    return `${sign}${(absoluteValue / 1_000_000).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} M`;
  }

  if (absoluteValue >= 1_000) {
    return `${sign}${(absoluteValue / 1_000).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} K`;
  }

  return `${sign}${absoluteValue.toLocaleString('en-US')}`;
}

function formatValue(field, value, tableView) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (DATE_FIELDS.has(field)) {
    return String(value);
  }

  if (tableView === TABLE_VIEW.GROWTH && GROWTH_PERCENTAGE_FIELDS.has(field) && typeof value === 'number') {
    return `${(value * 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}%`;
  }

  if (ABSOLUTE_CURRENCY_FIELDS.has(field) && typeof value === 'number') {
    return formatAbbreviatedNumber(value);
  }

  if (ABSOLUTE_DECIMAL_FIELDS.has(field) && typeof value === 'number') {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  if (ABSOLUTE_SHARE_FIELDS.has(field) && typeof value === 'number') {
    return formatAbbreviatedNumber(value);
  }

  if (typeof value === 'number') {
    return formatAbbreviatedNumber(value);
  }

  return String(value);
}

export default function IncomeStatementTable({
  rows = [],
  selectedStock,
  tableView = TABLE_VIEW.ABSOLUTE
}) {
  if (!rows.length) {
    return null;
  }

  const fieldOrder =
    tableView === TABLE_VIEW.GROWTH ? GROWTH_FIELD_ORDER : ABSOLUTE_FIELD_ORDER;
  const fieldLabels =
    tableView === TABLE_VIEW.GROWTH ? GROWTH_FIELD_LABELS : ABSOLUTE_FIELD_LABELS;
  const sortedRows = [...rows].sort((a, b) => Number(a.fiscalYear) - Number(b.fiscalYear));
  const years = sortedRows.map((row) => row.fiscalYear);
  const subtitle =
    tableView === TABLE_VIEW.GROWTH
      ? 'Growth percentages, grouped by metric and fiscal year.'
      : 'Full values, grouped by metric and fiscal year.';

  return (
    <section className="income-statement-table-card">
      <div className="income-statement-table-header">
        <div>
          <h3 className="income-statement-table-title">{selectedStock} Income Statement Table</h3>
          <p className="income-statement-table-subtitle">{subtitle}</p>
        </div>
      </div>

      <div className="income-statement-table-scroll">
        <table className="income-statement-table">
          <thead>
            <tr>
              <th className="income-statement-metric-column">Metric</th>
              {years.map((year) => (
                <th key={year}>{year}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fieldOrder.map((field) => (
              <tr key={field}>
                <td className="income-statement-metric-cell">
                  {fieldLabels[field] || field}
                </td>
                {sortedRows.map((row) => (
                  <td key={`${field}-${row.fiscalYear}`} className="income-statement-value-cell">
                    {formatValue(field, row[field], tableView)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
